<script setup lang="ts">
import { ref, watch } from 'vue'
import JLayout from './JLayout.vue'
import JHeader from '@/components/organisms/JHeader.vue'
import JSidebar from '@/components/organisms/JSidebar/JSidebar.vue'
import JPageContainer from '@/components/organisms/JPageContainer.vue'
import { useBreakpoint } from '@/composables/useBreakpoint'
import type { SidebarMenuItem } from '@/types/sidebar.types'

/**
 * JLayoutSimple - JSidebar + 일반 PageContainer 조합 템플릿
 *
 * 모바일에서는 사이드바가 오버레이 드로어로 전환되며,
 * 메뉴 클릭 시 자동으로 닫힙니다.
 *
 * @example
 * ```vue
 * <JLayoutSimple :menu-items="menuItems" storage-key="my-app-favorites">
 *   <template #content>
 *     <RouterView />
 *   </template>
 * </JLayoutSimple>
 * ```
 */

const props = withDefaults(
  defineProps<{
    /** 레이아웃 스타일 타입 */
    styletype?: 'default' | 'minimal'
    /** 콘텐츠 영역 스크롤 가능 여부 */
    contentScroll?: boolean
    /** 추가 CSS 클래스 */
    class?: string
    /** 메뉴 아이템 목록 */
    menuItems?: SidebarMenuItem[]
    /** 현재 활성 경로 */
    activePath?: string
    /** 즐겨찾기 localStorage 키 */
    storageKey?: string
  }>(),
  {
    styletype: 'default',
    contentScroll: true,
    menuItems: () => [],
  }
)

const emit = defineEmits<{
  'menu-click': [item: SidebarMenuItem, event: MouseEvent]
}>()

const { isMobile } = useBreakpoint()
const isSidebarCollapsed = ref(false)

// 모바일 전환 시 사이드바 자동 닫기
watch(isMobile, (mobile) => {
  if (mobile) isSidebarCollapsed.value = true
})

const handleSidebarToggle = () => {
  isSidebarCollapsed.value = !isSidebarCollapsed.value
}

const handleMenuClick = (item: SidebarMenuItem, event: MouseEvent) => {
  emit('menu-click', item, event)
  // 모바일에서 메뉴 클릭 시 사이드바 자동 닫기
  if (isMobile.value) isSidebarCollapsed.value = true
}

const handleBackdropClick = () => {
  isSidebarCollapsed.value = true
}
</script>

<template>
  <JLayout
    v-bind="props"
    :sidebar-open="!isSidebarCollapsed"
    @backdrop-click="handleBackdropClick"
  >
    <template #header>
      <slot name="header" :is-sidebar-collapsed="isSidebarCollapsed" :on-sidebar-toggle="handleSidebarToggle">
        <JHeader
          logo-text="JWMS Portal"
          :styletype="props.styletype"
          :show-sidebar-toggle="true"
          :is-sidebar-open="!isSidebarCollapsed"
          @sidebar-toggle="handleSidebarToggle"
        />
      </slot>
    </template>
    <template #sidebar>
      <slot name="sidebar" :is-sidebar-collapsed="isSidebarCollapsed">
        <JSidebar
          v-model:collapsed="isSidebarCollapsed"
          :items="props.menuItems"
          :active-path="props.activePath"
          :storage-key="props.storageKey"
          @menu-click="handleMenuClick"
        />
      </slot>
    </template>
    <template #content>
      <slot name="content">
        <slot>
          <!-- 기본 콘텐츠 예시 -->
          <JPageContainer title="페이지 제목">
            <div class="p-6">
              <p class="text-muted-foreground">콘텐츠를 배치해주세요.</p>
            </div>
          </JPageContainer>
        </slot>
      </slot>
    </template>
  </JLayout>
</template>
